import { View, Pressable, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Caption, Body } from '@/components/ui/Text';
import type { TrustedContact } from '@/services/safety';
import * as Linking from 'expo-linking';

type TrustedContactsListProps = {
  contacts: TrustedContact[];
  onEdit?: (contact: TrustedContact) => void;
  onDelete?: (contactId: string) => void;
  onCall?: (phoneNumber: string) => void;
};

export function TrustedContactsList({
  contacts,
  onEdit,
  onDelete,
  onCall,
}: TrustedContactsListProps) {
  const theme = useTheme();

  const handleCall = async (phoneNumber: string) => {
    if (onCall) {
      onCall(phoneNumber);
    } else {
      const telUrl = `tel:${phoneNumber.replace(/[^\d+]/g, '')}`;
      const canOpen = await Linking.canOpenURL(telUrl);
      if (canOpen) {
        await Linking.openURL(telUrl);
      }
    }
  };

  const handleDelete = (contact: TrustedContact) => {
    Alert.alert(
      'Delete Contact',
      `Are you sure you want to delete ${contact.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => onDelete?.(contact.id),
        },
      ]
    );
  };

  if (contacts.length === 0) {
    return (
      <View style={{ paddingVertical: theme.spacing(2) }}>
        <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
          No trusted contacts yet. Add one to get started.
        </Body>
      </View>
    );
  }

  return (
    <View style={{ gap: theme.spacing(1.5) }}>
      {contacts.map((contact) => (
        <View
          key={contact.id}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(1.5),
            backgroundColor: theme.colors.surface,
            borderRadius: theme.radii.md,
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
          }}
        >
          <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <ThemedText variant="bodyStrong">{contact.name}</ThemedText>
              {contact.isPrimary && (
                <View
                  style={{
                    backgroundColor: theme.colors.primary,
                    paddingHorizontal: theme.spacing(0.75),
                    paddingVertical: theme.spacing(0.25),
                    borderRadius: theme.radii.sm,
                  }}
                >
                  <Caption color={theme.colorScheme === 'dark' ? '#000000' : theme.colors.surface} style={{ fontSize: 10 }}>
                    Primary
                  </Caption>
                </View>
              )}
            </View>
            <Caption color={theme.colors.textSecondary}>{contact.phoneNumber}</Caption>
            {contact.relationship && (
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                {contact.relationship}
              </Caption>
            )}
          </View>

          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            {contact.phoneNumber && (
              <Pressable
                onPress={() => handleCall(contact.phoneNumber)}
                style={{
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.primary + '20',
                }}
              >
                <Ionicons name="call-outline" size={20} color={theme.colors.primary} />
              </Pressable>
            )}
            {onEdit && (
              <Pressable
                onPress={() => onEdit(contact)}
                style={{
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.md,
                }}
              >
                <Ionicons name="pencil-outline" size={20} color={theme.colors.textSecondary} />
              </Pressable>
            )}
            {onDelete && (
              <Pressable
                onPress={() => handleDelete(contact)}
                style={{
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.md,
                }}
              >
                <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
              </Pressable>
            )}
          </View>
        </View>
      ))}
    </View>
  );
}



